import { Media, MediaCaption } from "@/services/bedrock";

export function isSrtFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".srt");
}

function isEditedVideoFile(file: File): boolean {
  return file.type.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/i.test(file.name);
}

export function splitVideoAndSrtFiles(files: File[]): { videos: File[]; srts: File[] } {
  const videos: File[] = [];
  const srts: File[] = [];
  for (const file of files) {
    if (isSrtFile(file)) srts.push(file);
    else if (isEditedVideoFile(file)) videos.push(file);
  }
  return { videos, srts };
}

export function validateEditedDeliverableSelection(files: File[]): string | undefined {
  const { videos, srts } = splitVideoAndSrtFiles(files);
  if (videos.length > 1 || srts.length > 1) {
    return "Select only one video and one SRT file.";
  }
  if (files.length > 0 && videos.length === 0 && srts.length === 0) {
    return "Only video and SRT files are allowed.";
  }
  return undefined;
}

export function hasEditedSrt(media?: Media): boolean {
  const caption = media?.caption;
  return Boolean(caption?.content || caption?.uri || caption?.file);
}

export function hasEditedVideo(media?: Media): boolean {
  return Boolean(media?.imageFile || media?.uri);
}

export function hasRequiredEditedSrt(media?: Media[]): boolean {
  return Boolean(media?.some(hasEditedSrt));
}

function mediaFromEditedCaption(caption: MediaCaption, timestamp: number): Media {
  return {
    id: -timestamp,
    sortOrder: 0,
    category: "VIDEO",
    uri: "",
    type: "application/x-subrip",
    extension: "srt",
    caption,
  };
}

export function clearEditedSrt(media: Media[]): Media[] {
  if (!hasEditedVideo(media[0])) return [];
  return media.map((item, index) => {
    if (index !== 0) return item;
    const { caption: _caption, ...rest } = item;
    return rest;
  });
}

export function removeEditedVideo(media: Media[], timestamp = Date.now()): Media[] {
  const caption = media.find(hasEditedSrt)?.caption;
  if (!caption) return [];
  return [mediaFromEditedCaption(caption, timestamp)];
}

export function captionFileLabel(caption?: MediaCaption): string {
  if (!caption) return "Caption SRT file";
  if (caption.fileName) return caption.fileName;
  if (caption.file instanceof File) return caption.file.name;
  if (caption.uri) {
    try {
      const name = decodeURIComponent(new URL(caption.uri).pathname.split("/").pop() || "");
      if (name) return name.split("?")[0];
    } catch {
      const fallback = caption.uri.split("/").pop();
      if (fallback) return fallback.split("?")[0];
    }
  }
  return "Caption SRT file";
}

function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

async function captionFromSrtFile(file: File): Promise<MediaCaption> {
  return {
    content: await readFileText(file),
    file,
    fileName: file.name,
  };
}

function mediaFromEditedVideoFile(file: File, timestamp: number, caption?: MediaCaption): Media {
  const extension = file.type.split("/")[1] || file.name.split(".").pop() || "mp4";
  return {
    id: -timestamp,
    sortOrder: 0,
    category: "VIDEO",
    uri: URL.createObjectURL(file),
    type: file.type || `video/${extension}`,
    extension,
    imageFile: file,
    fileName: file.name.split(".")[0] + timestamp,
    caption,
  };
}

export async function applyEditedDeliverableFiles(
  current: Media[],
  files: File[],
  timestamp = Date.now()
): Promise<Media[]> {
  const { videos, srts } = splitVideoAndSrtFiles(files);
  const caption = srts[0] ? await captionFromSrtFile(srts[0]) : undefined;
  const existingCaption = hasEditedSrt(current[0]) ? current[0].caption : undefined;

  if (videos[0]) {
    return [mediaFromEditedVideoFile(videos[0], timestamp, caption ?? existingCaption)];
  }

  if (caption) {
    if (hasEditedVideo(current[0])) {
      return current.map((item, index) => (index === 0 ? { ...item, caption } : item));
    }
    return [mediaFromEditedCaption(caption, timestamp)];
  }

  return current;
}

export function toEditedMediaPayload(media: Media[]): Media[] {
  return media.filter(hasEditedVideo).map(item => {
    const { caption: _caption, ...rest } = item;
    return {
      ...rest,
      id: item.imageFile ? -1 : item.id,
    };
  });
}

export async function prepareEditedSubtitle(
  uploader: { uploadFile: (file: File, fileName: string, onProgress?: (progress: number) => void) => Promise<string> },
  media?: Media[],
  onProgress?: (progress: number) => void
): Promise<{ uri: string; content?: string } | undefined> {
  if (!media?.some(hasEditedVideo)) return undefined;
  const caption = media.find(hasEditedSrt)?.caption;
  if (!caption) return undefined;

  let content = caption.content;
  let uri = caption.uri;
  if (caption.file instanceof File) {
    content = content || (await readFileText(caption.file));
    uri = await uploader.uploadFile(caption.file, caption.fileName || caption.file.name, onProgress);
  }
  if (!uri) return undefined;
  return { uri, content };
}
